import { useEffect, useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/Button";
import { PageHeader, Panel } from "../components/Surface";
import { ListRow } from "../components/ListTable";
import { Results, ItemCard } from "../components/Paged";
import { FormRow, inputClass } from "../components/FormKit";
import { labelize } from "../lib/format";
import { FunctionsError } from "../lib/functions-client";
import { createTicket, listTickets, SUPPORT_CATEGORIES, type Ticket } from "../lib/support";

const TABS = [
  { key: "open", label: "Open" },
  { key: "closed", label: "Resolved" },
];

export function Support() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("open");
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [subject, setSubject] = useState("");
  const [category, setCategory] = useState("help");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    listTickets(tab).then((r) => setTickets(r.tickets)).finally(() => setLoading(false));
  }, [tab]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!subject.trim() || !body.trim()) return;
    setBusy(true);
    setNote(null);
    try {
      const { ticket_id } = await createTicket({ subject, body, category });
      navigate(`/support/${ticket_id}`);
    } catch (err) {
      setNote(err instanceof FunctionsError ? err.message : "Could not create the ticket.");
      setBusy(false);
    }
  }

  return (
    <div className="grid gap-6">
      <PageHeader
        title="Support"
        subtitle="Get help from the RecruitTrucker team."
        actions={<Button onClick={() => setShowNew((v) => !v)}>{showNew ? "Cancel" : "New ticket"}</Button>}
      />

      {showNew && (
        <Panel title="New ticket">
          <form className="grid gap-4" onSubmit={submit}>
            <FormRow label="Subject" required>
              <input className={inputClass} maxLength={200} value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Short summary" aria-label="Subject" required />
            </FormRow>
            <FormRow label="Category">
              <select className={inputClass} value={category} onChange={(e) => setCategory(e.target.value)} aria-label="Category">
                {SUPPORT_CATEGORIES.map((c) => <option key={c} value={c}>{labelize(c)}</option>)}
              </select>
            </FormRow>
            <FormRow label="Message" required>
              <textarea className={inputClass} rows={5} maxLength={5000} value={body} onChange={(e) => setBody(e.target.value)} placeholder="Tell us what's going on…" aria-label="Message" required />
            </FormRow>
            <div className="flex items-center gap-3">
              <Button type="submit" disabled={busy || !subject.trim() || !body.trim()}>{busy ? "Submitting…" : "Submit ticket"}</Button>
              {note && <span className="font-mono text-sm text-danger">{note}</span>}
            </div>
          </form>
        </Panel>
      )}

      <div className="flex flex-wrap gap-1 border-b border-line">
        {TABS.map((t) => (
          <button key={t.key} type="button" onClick={() => setTab(t.key)} className={`-mb-px border-b-2 px-3 py-2 text-sm font-bold transition-colors ${tab === t.key ? "border-accent text-accent-strong" : "border-transparent text-muted hover:text-ink"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <p className="font-mono text-sm text-muted">Loading…</p>
      ) : (
        <Results
          items={tickets}
          perPage={12}
          defaultView="list"
          listColumns={["Subject", "Category", "Updated"]}
          gridClass="grid gap-3 sm:grid-cols-2 lg:grid-cols-3"
          empty={<p className="font-mono text-sm text-muted">No {tab === "open" ? "open" : "resolved"} tickets.</p>}
          renderRow={(t) => (
            <ListRow key={t.id} to={`/support/${t.id}`} title={t.subject} meta={labelize(t.category)} status={t.status} right={new Date(t.updated_at).toLocaleDateString()} />
          )}
          renderCard={(t) => (
            <ItemCard key={t.id} to={`/support/${t.id}`} title={t.subject} meta={labelize(t.category)} status={t.status} right={new Date(t.updated_at).toLocaleDateString()} />
          )}
        />
      )}
    </div>
  );
}
